import React, { useEffect, useState, useContext, useCallback } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import moment from 'moment'
import { useAppDispatch } from '@/features/hooks'
import { editingSectionActions } from '@/features/reservation-detail/editing-section-slice'
import { updateSales, postPostClosingSalesAdjustment } from '@/apis/aipass'
import { NotConfirmedDiscount } from '@/components/molecules/customer/sales-manager/discount/not-confirmed-discount'
import { ConfirmedDiscount } from '@/components/molecules/customer/sales-manager/discount/confirmed-discount'
import { PostClosingConfirmedDiscount } from '@/components/molecules/customer/sales-manager/discount/post-closing-confirmed-discount'
import { PostClosingPaymentDateModal } from '@/components/molecules/customer/sales-manager/usage-detail/post-closing-payment-date-modal'
import { sectionHeaderStyle } from '@/components/organisms/dashboard/style'
import { Button } from '@/components/atoms/button'
import { simpleTableStyle } from '@/constants/layout'
import { DiscountSettingType, EditDiscountType, PaymentSettingType, SalesDiscountType } from '@/models/sales'
import { ReservationType } from '@/models/reservation'
import { AccountsReceivableSettingType } from '@/models/accounts-receivable'
import { ApprovedStatus } from '@/models/reservation-approved-status'
import { isDiscountChange } from '@/libs/sales-subjects'
import { NOT_ENOUGH_BALANCE_CODE } from '@/errors/company-advance-money-error'
import { useConfirmModal } from '@/components/organisms/customer/use-confirm-modal'
import { isItemDatesInStayPeriod } from '@/utils/reservation-period'
import { isStaffGuardCancelledError } from '@/libs/errors'
import { useSalesEditGuard } from '@/hooks/use-sales-edit-guard'
import { AccountContext } from '@/contexts/account'
import { canUseOperation } from '@/libs/hotel-pms-permission'
import {
  buildPostClosingDiscountAdjustmentParams,
  buildPostClosingDeleteParams,
  buildAdminSalesSavePayload,
  canShowPostClosingEditPencil,
  canUsePostClosingSalesApi,
  confirmClosedSalesDateSelection,
  getPostClosingAdjustmentAlertMessage,
  isPostClosingDiscountRowInputValid,
  shouldShowPostClosingDiscountPaymentModal,
  shouldShowPostClosingDiscountRow,
  syncDiscountPaymentNameFromMaster,
} from '@/libs/post-closing-sales-adjustment'
import type { PostClosingModalSubmitValues } from '@/models/post-closing-sales-adjustment'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { AdvanceCompanyType } from '@/models/sales-manager/advance-money'

type DiscountProps = {
  reservationId: string
  salesDiscounts: DiscountSettingType[]
  salesDiscountPayments: PaymentSettingType[]
  setIsLoading: (v: boolean) => void
  discounts: SalesDiscountType[]
  originDiscounts: SalesDiscountType[]
  setDiscounts: (v: SalesDiscountType[]) => void
  editDiscounts: EditDiscountType[]
  setEditDiscounts: (v: EditDiscountType[]) => void
  confirmedDay: string
  useCheckinDay: string
  accountsReceivableSettings: AccountsReceivableSettingType[]
  reservation: ReservationType
  salesDiscountPaymentsGroupBySalesDate?: any
  onUpdateDiscount: () => void | Promise<void>
  onRefreshSalesData: () => void | Promise<void>
  advanceCompany: AdvanceCompanyType
}

export const Discount: React.FC<DiscountProps> = ({
  reservationId,
  salesDiscounts,
  salesDiscountPayments,
  setIsLoading,
  discounts,
  setDiscounts,
  originDiscounts,
  editDiscounts,
  setEditDiscounts,
  confirmedDay,
  useCheckinDay,
  accountsReceivableSettings,
  reservation,
  salesDiscountPaymentsGroupBySalesDate,
  onUpdateDiscount,
  onRefreshSalesData,
  advanceCompany,
}) => {
  const { t } = useTranslation()
  const { openConfirmDialog, ConfirmDialog } = useConfirmModal()
  const dispatch = useAppDispatch()
  const { errorHandler } = useErrorHandler()
  const { permissions } = useContext<any>(AccountContext)
  const canPostClosingEdit = canUseOperation(permissions, 'post_closing_sales_edit')
  const [postClosingEditingIndex, setPostClosingEditingIndex] = useState<number | null>(null)
  const [postClosingEditingSnapshot, setPostClosingEditingSnapshot] = useState<SalesDiscountType | null>(null)
  const [postClosingPendingDelete, setPostClosingPendingDelete] = useState(false)
  const [isPostClosingModalOpen, setIsPostClosingModalOpen] = useState(false)

  const [deleteIds, setDeleteIds] = useState<{ id: string }[]>([])
  // Toggle edit mode
  const [isEditMode, setIsEditMode] = useState<boolean>(false)
  const { guardSalesEdit, createGuardedSetEditMode } = useSalesEditGuard()
  const setEditModeWithGuard = createGuardedSetEditMode(setIsEditMode)

  useEffect(() => {
    dispatch(
      editingSectionActions.setEditingSection({
        key: 'discount',
        value: isEditMode || postClosingEditingIndex !== null,
      }),
    )
    return () => {
      dispatch(editingSectionActions.setEditingSection({ key: 'discount', value: false }))
    }
  }, [isEditMode, postClosingEditingIndex, dispatch])
  const isPostClosingEditing = postClosingEditingIndex !== null

  // Edit Discount Item
  const changeDate = (date, index) => {
    if (!date) return
    const nextSalesDate = dayjs(date).format('YYYY-MM-DD')
    if (!confirmClosedSalesDateSelection(nextSalesDate, editDiscounts[index].salesDate, confirmedDay, t)) return
    const _editDiscounts = [...editDiscounts]
    _editDiscounts[index].salesDate = nextSalesDate
    addIsEdit(_editDiscounts[index])
    setEditDiscounts(_editDiscounts)
  }
  const changeState = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>, index) => {
    const _editDiscounts = [...editDiscounts]
    const value = e.target.value
    const name = e.target.name
    _editDiscounts[index][name] = value
    if (name === 'salesDiscountId') {
      overrideInputOnSelectedSubject(_editDiscounts[index], value)
    }

    addIsEdit(_editDiscounts[index])
    setEditDiscounts(_editDiscounts)
    // If there is a null salesPaymentId value in _editDiscounts, hyphenate it there
    if (_editDiscounts[index]['salesPaymentId'] === '') {
      _editDiscounts[index]['salesPaymentId'] = salesDiscountPayments[0]['id']
    }
  }

  // Edit unconfirmed course
  const changeNotConfirmedDate = (date, index) => {
    if (!date) return
    const nextSalesDate = dayjs(date).format('YYYY-MM-DD')
    if (!confirmClosedSalesDateSelection(nextSalesDate, discounts[index].salesDate, confirmedDay, t)) return
    const _discounts = [...discounts]
    _discounts[index].salesDate = nextSalesDate
    addIsEdit(_discounts[index])
    setDiscounts(_discounts)
  }
  const changeNotConfirmedState = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>, index) => {
    const _discounts = [...discounts]
    const value = e.target.value
    const name = e.target.name
    _discounts[index][name] = value
    if (name === 'salesDiscountId') {
      overrideInputOnSelectedSubject(_discounts[index], value)
    }
    addIsEdit(_discounts[index])
    setDiscounts(_discounts)
  }

  const addDiscount = () => {
    if (!guardSalesEdit()) return
    setIsEditMode(true)

    const isStayOrCO = reservation?.approvedStatus === +ApprovedStatus.Stay || reservation?.approvedStatus === +ApprovedStatus.Checkout

    const newDiscounts: EditDiscountType = {
      salesDate: isStayOrCO ? dayjs().format('YYYY-MM-DD') : useCheckinDay,
      salesDiscountId: '',
      salesSubjectPrice: 0,
      salesPaymentId: '',
      reservationId,
      isEdit: true,
      isDiscount: true,
      guestRoomAssignId: reservation.assignedRoom[0]?.guestRoomAssignId,
    }
    setEditDiscounts([...editDiscounts, newDiscounts])
  }

  const deleteDiscount = (index: number) => {
    const newDetails = [...discounts]
    newDetails.splice(index, 1)
    setDiscounts(newDetails)
    setDeleteIds([...deleteIds, { id: discounts[index].id }])
  }

  const deleteDiscountSubject = index => {
    const newSubjectDetails = [...editDiscounts]
    newSubjectDetails.splice(index, 1)
    setEditDiscounts(newSubjectDetails)
  }

  const markPostClosingForDelete = useCallback(() => {
    setPostClosingPendingDelete(true)
  }, [])

  const cancelPostClosingEdit = useCallback(() => {
    if (postClosingEditingIndex !== null && postClosingEditingSnapshot) {
      const restored = [...discounts]
      restored[postClosingEditingIndex] = { ...postClosingEditingSnapshot }
      setDiscounts(restored)
    }
    setPostClosingEditingIndex(null)
    setPostClosingEditingSnapshot(null)
    setPostClosingPendingDelete(false)
    setIsPostClosingModalOpen(false)
  }, [postClosingEditingIndex, postClosingEditingSnapshot, discounts, setDiscounts])

  const submitPostClosingAdjustment = useCallback(
    async (modalValues?: PostClosingModalSubmitValues) => {
      if (postClosingEditingIndex === null) return
      const discount = discounts[postClosingEditingIndex]
      // 編集後の状態ではなく、編集開始時（origin）で対象判定する（精算済→「-」でも弾かない）
      const originForContext = postClosingEditingSnapshot ?? discount
      if (!canUsePostClosingSalesApi(originForContext, salesDiscountPayments)) {
        alert(t('HotelPms.PostClosing.notPostClosingContext'))
        return
      }
      const payload = postClosingPendingDelete
        ? buildPostClosingDeleteParams(discount.id)
        : buildPostClosingDiscountAdjustmentParams(discount, modalValues, postClosingEditingSnapshot ?? undefined, salesDiscountPayments)

      setIsLoading(true)
      try {
        await postPostClosingSalesAdjustment(payload)
        try {
          await onRefreshSalesData()
        } finally {
          setPostClosingEditingIndex(null)
          setPostClosingEditingSnapshot(null)
          setPostClosingPendingDelete(false)
          setIsPostClosingModalOpen(false)
        }
      } catch (error: any) {
        if (isStaffGuardCancelledError(error)) return
        const postClosingMessage = getPostClosingAdjustmentAlertMessage(error, t)
        if (postClosingMessage) {
          alert(postClosingMessage)
          return
        }
        errorHandler(error)
      } finally {
        setIsLoading(false)
      }
    },
    [
      postClosingEditingIndex,
      postClosingEditingSnapshot,
      postClosingPendingDelete,
      discounts,
      salesDiscountPayments,
      t,
      errorHandler,
      onRefreshSalesData,
      setIsLoading,
    ],
  )

  const savePostClosingEdit = useCallback(() => {
    if (postClosingEditingIndex === null) return

    if (postClosingPendingDelete) {
      void submitPostClosingAdjustment()
      return
    }

    const discount = discounts[postClosingEditingIndex]

    if (!isPostClosingDiscountRowInputValid(discount)) {
      alert(t('Select the discount item and eligible payment Please enter the amount in single-byte numbers'))
      return
    }

    // 未精算相当（対象支払いが「-」）なら入金日時モーダルは出さない
    if (shouldShowPostClosingDiscountPaymentModal(discount, salesDiscountPayments)) {
      setIsPostClosingModalOpen(true)
      return
    }

    void submitPostClosingAdjustment()
  }, [postClosingEditingIndex, postClosingPendingDelete, discounts, salesDiscountPayments, t, submitPostClosingAdjustment])

  const clearNormalEditState = useCallback(() => {
    setDiscounts(originDiscounts.map(value => ({ ...value })))
    setEditDiscounts([])
    setDeleteIds([])
    setIsEditMode(false)
  }, [originDiscounts, setDiscounts, setEditDiscounts])

  const startPostClosingEdit = useCallback(
    (index: number) => {
      const discountId = discounts[index]?.id
      const originDiscount = discountId ? originDiscounts.find(item => item.id === discountId) : discounts[index]
      if (!originDiscount) return
      clearNormalEditState()
      const restored = originDiscounts.map(value => ({ ...value }))
      const displayIndex = discountId ? restored.findIndex(item => item.id === discountId) : index
      if (displayIndex < 0) return
      setPostClosingEditingSnapshot({ ...originDiscount })
      setPostClosingEditingIndex(displayIndex)
      setPostClosingPendingDelete(false)
    },
    [discounts, originDiscounts, clearNormalEditState],
  )

  const changePostClosingDate = (date: moment.Moment | null, index: number) => {
    if (!date) return
    const nextSalesDate = date.format('YYYY-MM-DD')
    if (!confirmClosedSalesDateSelection(nextSalesDate, discounts[index].salesDate, confirmedDay, t)) return
    const _discounts = [...discounts]
    _discounts[index].salesDate = nextSalesDate
    setDiscounts(_discounts)
  }

  const changePostClosingState = (e: { target: { value: string; name: string } }, index: number) => {
    const _discounts = [...discounts]
    const value = e.target.value
    const name = e.target.name
    _discounts[index][name] = value

    if (name === 'salesDiscountId') {
      overrideInputOnSelectedSubject(_discounts[index], value)
    }
    if (name === 'salesPaymentId') {
      syncDiscountPaymentNameFromMaster(_discounts[index], salesDiscountPayments)
    }

    setDiscounts(_discounts)
  }

  // Newly created and edited item flags
  const addIsEdit = (array: SalesDiscountType | EditDiscountType) => {
    const discountById = originDiscounts.find(originDiscount => originDiscount.id === array.id)
    const discountWithoutIsEdit = { ...array }
    delete discountWithoutIsEdit['isEdit']
    if (isDiscountChange(discountById, discountWithoutIsEdit)) {
      array['isEdit'] = true
    } else {
      array['isEdit'] = false
    }
  }

  const isDirtySalesOnConfirmed = (originSales: SalesDiscountType, editSales: SalesDiscountType | EditDiscountType): boolean => {
    if (!originSales.isConfirm) {
      return false
    }
    return originSales.salesDate !== editSales.salesDate || Number(originSales.salesSubjectPrice) !== Number(editSales.salesSubjectPrice)
  }

  const saveDiscount = async () => {
    if (!guardSalesEdit()) return
    // Deep copy modified and newly created data
    const _editDiscounts = [...discounts, ...editDiscounts].map(value => ({ ...value }))
    // Save array: data that has not been changed or newly created is deleted from the array
    const saveDiscounts = _editDiscounts.filter(discount => discount['isEdit'] === true)
    let isEditConfirmedSales = false

    // Discounts that change the target payment will set the payment date (paymentSalesManagerDailyId) to null
    originDiscounts.forEach(originDiscount => {
      if (deleteIds.findIndex(({ id }) => id === originDiscount.id) !== -1 && originDiscount.isConfirm) {
        isEditConfirmedSales = true
        return
      }
      // Get the edit item index with the same id as "original data id"
      const indexOfSameId = saveDiscounts.findIndex(sales => sales.id === originDiscount.id)
      if (indexOfSameId === -1) return
      isEditConfirmedSales = isEditConfirmedSales || isDirtySalesOnConfirmed(originDiscount, saveDiscounts[indexOfSameId])
      // If "target payment for original data" and "target payment for modified data" are different, set the payment date to null
      if (saveDiscounts[indexOfSameId]['salesPaymentId'] !== originDiscount.salesPaymentId) {
        saveDiscounts[indexOfSameId]['paymentSalesManagerDailyId'] = null
      }
    })

    if (!isItemDatesInStayPeriod(saveDiscounts as SalesDiscountType[], [reservation]) && !(await openConfirmDialog())) {
      return
    }

    saveDiscounts.forEach(saveDiscount => {
      saveDiscount['salesSubjectPrice'] = Number(saveDiscount['salesSubjectPrice']) || 0
    })

    // If the saved data is caught in validation, display an alert and end the saving process
    if (checkCompletedInput(saveDiscounts)) {
      alert(t('Select the discount item and eligible payment Please enter the amount in single-byte numbers'))
      return
    }

    // Combining "create new, modify" and "delete" (data sent to API)
    const updateDiscounts = [...saveDiscounts, ...deleteIds]

    // If there is no data, exit edit mode and end save processing
    if (updateDiscounts.length === 0) {
      setIsEditMode(false)
      return
    }

    if (
      isEditConfirmedSales &&
      !window.confirm(t('The date on which the closing process was closed. Sales totals will change, do you want to save them?'))
    ) {
      return
    }

    const adminSalesPayload = buildAdminSalesSavePayload(saveDiscounts as SalesDiscountType[], deleteIds, originDiscounts)

    if (adminSalesPayload.length === 0) {
      setIsEditMode(false)
      return
    }

    setIsLoading(true)
    updateSales(adminSalesPayload)
      .then(async () => {
        try {
          await onUpdateDiscount()
        } finally {
          setEditDiscounts([])
          setDeleteIds([])
          setIsEditMode(false)
          setIsLoading(false)
        }
      })
      .catch(error => {
        if (isStaffGuardCancelledError(error)) return
        if (error?.response?.data?.errorCode === NOT_ENOUGH_BALANCE_CODE) {
          alert(t('Insufficient balance Please check again'))
        } else {
          console.log(t('Communication failed'))
          setEditDiscounts([])
          setIsEditMode(false)
        }
        setIsLoading(false)
      })
  }

  // Cancellation process
  const resetDiscount = () => {
    clearNormalEditState()
  }

  // Are the subject and payment method entered?Are the subject and payment method entered?
  const checkCompletedInput = (editDiscounts: (SalesDiscountType | EditDiscountType)[]) => {
    const isEmptySalesSubjectId = editDiscounts.some(editDiscount => editDiscount.salesDiscountId === '')
    const isEmptySalesPaymentId = editDiscounts.some(editDiscount => editDiscount.salesPaymentId === '')
    // Discount amount : salesSubjectPrice
    const isHalfSizeNumberSalesSubjectPrice = editDiscounts.some(editDiscount => !String(editDiscount.salesSubjectPrice).match(/^[0-9]+$/))
    if (isEmptySalesSubjectId || isEmptySalesPaymentId || isHalfSizeNumberSalesSubjectPrice) {
      return true
    } else {
      return false
    }
  }

  const overrideInputOnSelectedSubject = (discount: SalesDiscountType | EditDiscountType, selectedDiscountId: string) => {
    const selectedDiscount = salesDiscounts.find(discount => {
      return discount.id === selectedDiscountId
    })
    const alignmentAccountReceivable = accountsReceivableSettings?.find(receivable => {
      return receivable.code === selectedDiscount?.accountsReceivableCode
    })
    discount['salesAccountsReceivableMasterId'] = alignmentAccountReceivable ? alignmentAccountReceivable.id : ''
  }

  return (
    <div css={leftSectionStyle}>
      <div css={headerContainerStyle}>
        <div css={leftContainerStyle}>
          <div css={headerStyle}>{t('Discount')}</div>
        </div>
      </div>
      <table css={simpleTableStyle} style={{ tableLayout: 'fixed' }}>
        <thead>
          <tr>
            <th css={{ width: '112px' }}>{t('Date')}</th>
            <th css={{ width: '18.36%' }}>{t('Discount item')}</th>
            <th css={{ width: '16.14%' }}>{`${t('Room number')}(${t('Room ID')})`}</th>
            <th css={{ width: '18.36%' }}>{t('Eligible payment')}</th>
            <th css={{ width: '9.58%' }}>{t('Amount of money')}</th>
            <th css={{ width: '18.36%' }}>{t('Accounts receivable')}</th>
            <th css={{ width: '6.37%' }}></th>
          </tr>
        </thead>
        <tbody>
          {discounts &&
            discounts.map((discount, index) => {
              // FIXME
              // eslint-disable-next-line no-prototype-builtins
              if (!discount.hasOwnProperty('salesDate')) return null
              const isPostClosingEditingRow = postClosingEditingIndex === index
              const showPostClosingFixedRow = shouldShowPostClosingDiscountRow(discount, isPostClosingEditingRow)
              const canEditThisPostClosingRow = canPostClosingEdit && canShowPostClosingEditPencil(discount)
              return (
                <React.Fragment key={index}>
                  {showPostClosingFixedRow ? (
                    isPostClosingEditingRow ? (
                      <PostClosingConfirmedDiscount
                        key={`post-closing-discount-edit-${index}`}
                        index={index}
                        discount={discount}
                        advanceCompany={advanceCompany}
                        onMarkDelete={markPostClosingForDelete}
                        onCancelEdit={cancelPostClosingEdit}
                        onSaveEdit={savePostClosingEdit}
                        pendingDelete={postClosingPendingDelete}
                        salesDiscounts={salesDiscounts}
                        salesDiscountPayments={salesDiscountPayments}
                        confirmedDay={confirmedDay}
                        changePostClosingDate={changePostClosingDate}
                        changePostClosingState={changePostClosingState}
                        accountsReceivableSettings={accountsReceivableSettings}
                        reservations={[reservation]}
                        currentTabReservationId={reservationId}
                      />
                    ) : (
                      <ConfirmedDiscount
                        key={`confirmed-discount-${index}`}
                        index={index}
                        discount={discount}
                        advanceCompany={advanceCompany}
                        canPostClosingEdit={canEditThisPostClosingRow}
                        onStartPostClosingEdit={() => startPostClosingEdit(index)}
                      />
                    )
                  ) : (
                    <NotConfirmedDiscount
                      key={`edit-discount-${index}`}
                      index={index}
                      discount={discount}
                      salesDiscounts={salesDiscounts}
                      salesDiscountPayments={salesDiscountPayments}
                      confirmedDay={confirmedDay}
                      allowClosedDates={canPostClosingEdit}
                      isEditMode={isEditMode}
                      changeNotConfirmedDate={changeNotConfirmedDate}
                      changeNotConfirmedState={changeNotConfirmedState}
                      deleteDiscount={deleteDiscount}
                      setIsEditMode={setEditModeWithGuard}
                      accountsReceivableSettings={accountsReceivableSettings}
                      reservation={reservation}
                      advanceCompany={advanceCompany}
                      salesDiscountPaymentsGroupBySalesDate={salesDiscountPaymentsGroupBySalesDate}
                    />
                  )}
                </React.Fragment>
              )
            })}
          {/* Newly added discount items */}
          {!isPostClosingEditing &&
            editDiscounts?.map((editDiscount, index) => (
              <NotConfirmedDiscount
                key={`add-discount-${index}`}
                index={index}
                discount={editDiscount}
                salesDiscounts={salesDiscounts}
                salesDiscountPayments={salesDiscountPayments}
                confirmedDay={confirmedDay}
                allowClosedDates={canPostClosingEdit}
                isEditMode={isEditMode}
                changeNotConfirmedDate={changeDate}
                changeNotConfirmedState={changeState}
                deleteDiscount={deleteDiscountSubject}
                setIsEditMode={setEditModeWithGuard}
                accountsReceivableSettings={accountsReceivableSettings}
                reservation={reservation}
                advanceCompany={advanceCompany}
                salesDiscountPaymentsGroupBySalesDate={salesDiscountPaymentsGroupBySalesDate}
              />
            ))}
        </tbody>
      </table>
      <div css={borderStyle}>
        {!isPostClosingEditing && (
          <div css={addRoomListWrapperOpenHasRoomManagerPluginStyle} onClick={addDiscount}>
            <img src={require('@/static/images/plus_yellow.svg')} alt={t('Add icon yellow')} />
            <p>{t('Add Discount Item')}</p>
          </div>
        )}
      </div>
      {isEditMode && !isPostClosingEditing && (
        <div css={saveButtonStyle}>
          <Button buttonType={3} width={80} height={32} fontSize={12} marginRight={14} marginBottom={16} onClick={resetDiscount}>
            {t('Cancel')}
          </Button>
          <Button width={80} height={32} buttonType={1} fontSize={12} marginRight={32} marginBottom={16} onClick={saveDiscount}>
            {t('Save')}
          </Button>
        </div>
      )}
      <ConfirmDialog />
      {isPostClosingModalOpen && postClosingEditingIndex !== null && (
        <PostClosingPaymentDateModal
          usageDetail={discounts[postClosingEditingIndex]}
          onClose={cancelPostClosingEdit}
          onSave={values => {
            void submitPostClosingAdjustment(values)
          }}
        />
      )}
    </div>
  )
}

const salesItemStyle = css({
  display: 'flex',
  fontSize: 14,
})

const leftSectionStyle = css(salesItemStyle, {
  width: '74%',
  minWidth: 600,
  border: '1px solid #CCCCCC',
  borderRadius: 5,
  flexDirection: 'column',
  height: 'fit-content',
})

const headerStyle = css(sectionHeaderStyle, {
  height: 50,
  marginLeft: 28,
  '@media(max-width: 1080px)': {
    marginLeft: 16,
  },
})

const headerContainerStyle = css({
  marginRight: 32,
  display: 'flex',
  justifyContent: 'space-between',
})

const leftContainerStyle = css({
  display: 'flex',
  float: 'left',
  alignItems: 'center',
})

const borderStyle = css({
  borderTop: '1px solid ##F2F2F2',
})

const addRoomListWrapperStyle = css({
  cursor: 'pointer',
  display: 'flex',
  height: 36,
  backgroundColor: '#FAFAFA',
  justifyContent: 'center',
  border: '1px dashed #CCCCCC',
  p: {
    lineHeight: '36px',
    color: '#F2A40B',
    fontSize: 12,
    fontWeight: 'bold',
    paddingLeft: 8,
  },
})

const addRoomListWrapperOpenHasRoomManagerPluginStyle = css(addRoomListWrapperStyle, {
  margin: '32px 24px',
  img: {
    width: 20,
  },
  ':hover': {
    background: '#F2F2F2',
  },
})

const saveButtonStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
  marginBottom: '24px',
  paddingBottom: '8px',
})
